import { Minus, TrendingUp, TrendingDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface KPIBoxProps {
  label: string;
  value: string | number;
  // Null or absent means there is nothing to compare against, and no trend is shown at all — not a
  // "↑ 0%", which is what every tile used to display whatever the data.
  change?: number | null;
  // 'points' for a rate (40% -> 50% is +10 pp), 'percent' for a count.
  changeUnit?: 'percent' | 'points';
  // Which direction is good news. A rise in cost per reply is not.
  betterWhen?: 'higher' | 'lower';
  changeLabel?: string;
  icon?: React.ReactNode;
}

export default function KPIBox({
  label,
  value,
  change,
  changeUnit = 'percent',
  betterWhen = 'higher',
  changeLabel = 'vs last month',
  icon,
}: KPIBoxProps) {
  const hasChange = typeof change === 'number' && Number.isFinite(change);
  const flat = hasChange && change === 0;
  const good = hasChange && !flat && ((change > 0) === (betterWhen === 'higher'));
  const Icon = !hasChange || flat ? Minus : change > 0 ? TrendingUp : TrendingDown;

  return (
    <Card className="p-6 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <p className="text-sm text-muted-foreground mb-1">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        {icon && (
          <div className="bg-gradient-primary p-2 rounded-lg">
            {icon}
          </div>
        )}
      </div>
      {hasChange && (
        <div className="flex items-center gap-1">
          <Icon
            className={cn('h-4 w-4', flat ? 'text-muted-foreground' : good ? 'text-success' : 'text-destructive')}
          />
          <span
            className={cn(
              'text-sm font-medium',
              flat ? 'text-muted-foreground' : good ? 'text-success' : 'text-destructive'
            )}
          >
            {Math.abs(change)}{changeUnit === 'points' ? ' pp' : '%'}
          </span>
          <span className="text-sm text-muted-foreground">{changeLabel}</span>
        </div>
      )}
    </Card>
  );
}
